import { union, uniq } from 'lodash-es'
import { isNonEmptyArray } from '#app/lib/boolean_tests'
import { icon as iconFn } from '#app/lib/icons'
import { workAuthorProperties, type AuthorProperty } from '#entities/lib/editor/properties_per_subtype'
import type { SerializedEntity } from '#entities/lib/entities'
import { localizeDateString } from '#entities/lib/localize_date_string'
import { platforms } from '#entities/lib/platforms'
import { isEntityType, typeDefaultP31 } from '#entities/lib/types/entities_types'
import type { RelativeUrl } from '#server/types/common'
import type { Claims, DateValue, EntityUri, ExtendedEntityType, InvClaimValue, PropertyUri, SimplifiedClaims, ClaimPropertyUri, ClaimValueByProperty } from '#server/types/entity'
import type { WikimediaLanguageCode } from 'wikibase-sdk'

interface FormatClaimValueParams {
  value: InvClaimValue
  prop: PropertyUri
  format?: string
}

export function formatClaimValue (params: FormatClaimValueParams) {
  const { value, prop } = params
  const propType = propertiesType[prop]
  if (propType && claimFormats[propType]) {
    return claimFormats[propType](params)
  } else if (prop && propertiesType[prop]) {
    const type = propertiesType[prop]
    return claimFormats[type](params)
  } else {
    return value
  }
}

export const propertiesByRoles: Record<string, AuthorProperty[]> = {
  // The keys order determines the display order in AuthorsInfo
  editor: [ 'wdt:P98' ],
  author: [ 'wdt:P50' ],
  scenarist: [ 'wdt:P58' ],
  illustrator: [ 'wdt:P110' ],
  penciller: [ 'wdt:P10837' ],
  colorist: [ 'wdt:P6338' ],
  letterer: [ 'wdt:P9191' ],
  inker: [ 'wdt:P10836' ],
  translator: [ 'wdt:P655' ],
  'author of foreword': [ 'wdt:P2679' ],
  'author of afterword': [ 'wdt:P2680' ],
  'cover artist': [ 'wdt:P736' ],
}

export const relativeEntitiesListsProps = [
  'wdt:P737', // influenced by
] as ClaimPropertyUri[]

export const editionWorkProperties = [
  ...workAuthorProperties,
  'wdt:P179', // serie
] as ClaimPropertyUri[]

export function aggregateWorksRelationsClaims (works: SerializedEntity[]) {
  const worksClaims = {} as SimplifiedClaims
  for (const work of works) {
    for (const prop of editionWorkProperties) {
      if (work.claims != null && isNonEmptyArray(work.claims[prop])) {
        // @ts-expect-error
        worksClaims[prop] ??= [] as EntityUri[]
        // @ts-expect-error
        worksClaims[prop] = union(worksClaims[prop], work.claims[prop])
      }
    }
  }
  return worksClaims
}

export const propertiesType = {
  'wdt:P577': 'timeClaim',
  'wdt:P856': 'urlClaim',
  'wdt:P953': 'urlClaim',
  'wdt:P1581': 'urlClaim',
  'wdt:P2034': 'platformClaim',
  'wdt:P724': 'platformClaim',
  'wdt:P4258': 'platformClaim',
} as Record<PropertyUri, 'timeClaim' | 'urlClaim' | 'platformClaim'>

export function buildPathname (entity: SerializedEntity, prop: PropertyUri) {
  const { uri, type } = entity

  if (isEntityType(type)) {
    return `/entity/${uri}` as RelativeUrl
  }
  if (!specialPathnameProperties.includes(prop)) prop = 'wdt:P921'
  return `/entity/${prop}-${uri}` as RelativeUrl
}

export const inverseLabels = {
  // Hardcoding as a make-it-work implementation for translation purposes (not to be updated too often).
  'wdt:P69': 'educated_at',
  'wdt:P110': 'Editions illustrated by %{name}',
  'wdt:P135': 'associated_with_this_movement',
  'wdt:P136': 'works_in_this_genre',
  'wdt:P144': 'works_based_on_work',
  'wdt:P655': 'editions_translated_by_author',
  'wdt:P674': 'character_in',
  'wdt:P736': 'Editions with %{name} for cover artist',
  'wdt:P737': 'authors_influenced_by',
  'wdt:P840': 'narrative_set_in_this_location',
  'wdt:P921': 'works_about_entity',
  'wdt:P941': 'works_inspired_by_work',
  'wdt:P1433': 'published_in',
  'wdt:P2675': 'works_replying_to_work',
  'wdt:P2679,wdt:P2680': 'editions_prefaced_or_postfaced_by_author',
  'wdt:P7937': 'works_in_this_form',
} as const

const specialPathnameProperties = Object.keys(inverseLabels)

export function timeClaim (params: Pick<FormatClaimValueParams, 'value' | 'format'>) {
  const { format } = params
  const value = params.value as string
  if (format && format === 'year') {
    const splittedDate = value.split('-')
    // Display before christ values with year precision only
    // Convert -427-05-07 to -427
    if (value.startsWith('-')) {
      return `-${splittedDate[1]}`
    }
    const year = parseInt(splittedDate[0])
    let unixTime = new Date(year.toString())
    if (year < 1000) {
      unixTime = new Date('0000')
      unixTime.setFullYear(year)
    }
    return unixTime.getUTCFullYear()
  } else {
    return localizeDateString(value)
  }
}

export function urlClaim (params: FormatClaimValueParams) {
  return removeTailingSlash(dropProtocol(params.value as string))
}

export function platformClaim (params: FormatClaimValueParams) {
  const value = params.value as string
  const prop = params.prop as keyof typeof platforms
  const platform = platforms[prop]
  const icon = iconFn(platform.icon)
  const text = icon + '<span>' + platform.text(value) + '</span>'
  const url = platform.url(value)
  return { icon, text, url }
}

const claimFormats = {
  timeClaim,
  urlClaim,
  platformClaim,
}

const removeTailingSlash = (url: string) => url.replace(/\/$/, '')
const dropProtocol = (url: string) => url.replace(/^(https?:)?\/\//, '')

export function getEntityFirstClaimValue <T extends ClaimPropertyUri> (entity: SerializedEntity, prop: T) {
  const claimValues = entity?.claims[prop]
  if (claimValues) return claimValues[0] as ClaimValueByProperty[T]
}

export function getEntityLangUris (entity: SerializedEntity) {
  return entity.claims['wdt:P407'] as EntityUri[]
}

export const hasSelectedLang = (selectedLang: WikimediaLanguageCode | 'all') => (edition: SerializedEntity) => {
  if (selectedLang === 'all') return true
  const { originalLang } = edition
  return originalLang != null ? selectedLang === originalLang : false
}

const workAndSeriesPropertiesByType = [
  ...workAuthorProperties,
  'wdt:P577', // publication date
  'wdt:P179', // series
  'wdt:P7937', // form of creative work
  'wdt:P136', // genre
  'wdt:P921', // main subject
] satisfies PropertyUri[]

export const infoboxShortlistPropertiesByType = {
  edition: [
    'wdt:P577', // publication date
    'wdt:P123', // publisher
    'wdt:P195', // collection
    'wdt:P437', // distribution format
    'wdt:P212', // ISBN-13
  ],
  human: [
    'wdt:P742', // pseudonym
    'wdt:P135', // movement
    'wdt:P27', // country of citizenship
    'wdt:P1412', // language of expression
    'wdt:P69', // educated at
    'wdt:P106', // occupation
  ],
  work: workAndSeriesPropertiesByType,
  serie: workAndSeriesPropertiesByType,
  publisher: [],
  collection: [],
  movement: [],
  genre: [],
  language: [],
  article: [],
  subject: [],
  meta: [],
} satisfies Record<ExtendedEntityType, PropertyUri[]>

const websites = [
  'wdt:P856', // official website
  'wdt:P1581', // official blog URL
] satisfies PropertyUri[]

const workProperties = [
  ...workAuthorProperties,
  'wdt:P577', // publication date
  'wdt:P7937', // form of creative work
  'wdt:P136', // genre
  'wdt:P361', // part of (serie)
  'wdt:P179', // series
  'wdt:P1545', // series ordinal
  'wdt:P1476', // title
  'wdt:P407', // edition language
  'wdt:P1680', // subtitle
  'wdt:P144', // based on
  'wdt:P2675', // reply to
  'wdt:P941', // inspired by
  'wdt:P135', // movement
  'wdt:P921', // main subject
  'wdt:P840', // narrative location
  'wdt:P674', // characters
  'wdt:P1433', // published in
  'wdt:P155', // preceded by
  'wdt:P156', // followed by
  ...websites,
] satisfies PropertyUri[]

export const infoboxPropertiesByType = {
  edition: [
    'wdt:P2679', // author of foreword
    'wdt:P2680', // author of afterword
    'wdt:P655', // translator
    'wdt:P577', // publication date
    'wdt:P123', // publisher
    'wdt:P195', // collection
    'wdt:P437', // distribution format
    'wdt:P1104', // number of pages
    'wdt:P212', // ISBN-13
    'wdt:P957', // ISBN-10
    'wdt:P629', // edition or translation of
    'wdt:P2635', // number of volumes
    ...websites,
    'wdt:P407', // edition language
  ],
  work: workProperties,
  serie: workProperties,
  human: [
    'wdt:P527', // has part(s), typically intended for collectives currently typed as humans
    'wdt:P742', // pseudonym
    'wdt:P135', // movement
    'wdt:P136', // genre
    'wdt:P27', // country of citizenship
    'wdt:P103', // native language
    'wdt:P1412', // language of expression
    'wdt:P69', // educated at
    'wdt:P106', // occupation
    'wdt:P166', // award received
    'wdt:P39', // position held
    'wdt:P1066', // student of
    'wdt:P737', // influenced by
    ...websites,
  ],
  publisher: [
    'wdt:P571', // inception
    'wdt:P576', // dissolution
    'wdt:P112', // founded by
    'wdt:P127', // owned by
    ...websites,
    'wdt:P407', // language
    'wdt:P136', // genre
    'wdt:P921', // subject
  ],
  collection: [
    'wdt:P123', // publisher
    'wdt:P571', // inception
    'wdt:P98', // editor
    'wdt:P112', // founded by
    'wdt:P127', // owned by
    'wdt:P407', // language
    ...websites,
    'wdt:P921', // subject
  ],
  article: [
    'wdt:P1433', // published in
    'wdt:P577', // publication date
    'wdt:P407', // language
    'wdt:P921', // subject
  ],
  movement: [],
  genre: [],
  language: [],
  subject: [],
  meta: [],
} satisfies Record<ExtendedEntityType, PropertyUri[]>

export function formatYearClaim (dateProp: keyof Claims, claims: Claims) {
  const values = claims[dateProp] as DateValue[]
  return isNonEmptyArray(values) ? uniq(values.map(formatTime)) : ''
}

const formatTime = (value: DateValue) => timeClaim({ value, format: 'year' })

export function formatEbooksClaim (values: string[], prop: PropertyUri) {
  if (!values) return
  return values.map(value => formatClaimValue({ prop, value }))
}

export const defaultWorkP31PerSerieP31 = {
  'wd:Q1667921': 'wd:Q7725634', // novel
  'wd:Q14406742': 'wd:Q1004', // comic book
  'wd:Q21198342': 'wd:Q8274', // manga
  'wd:Q74262765': 'wd:Q562214', // manhwa
  'wd:Q104213567': 'wd:Q747381', // light novel
} as Record<EntityUri, EntityUri>

export function getDefaultWorkP31FromSerie (serie: SerializedEntity) {
  const P31Value = serie.claims['wdt:P31']![0]
  return P31Value in defaultWorkP31PerSerieP31 ? defaultWorkP31PerSerieP31[P31Value] : typeDefaultP31.work
}
